import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { find, keys } from 'lodash';
import { breakpoints } from 'config/breakpoints';
import { getWindowWidth, on, off } from 'utils/dom/dom';

const determineScreenSize = (width) => {
	const screenWidths = keys(breakpoints).sort(
		(a, b) => parseInt(b, 10) - parseInt(a, 10),
	);
	const breakWidth = find(
		screenWidths,
		(w) => parseInt(w, 10) <= parseInt(width, 10),
	);
	return breakpoints[breakWidth];
};

/**
 * Custom hook for tracking viewport width and breakpoint.
 * @returns {{ viewportWidth: number, breakpoint: string }}
 */
const useIsomorphicLayoutEffect =
	typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const useViewportWidth = () => {
	const [viewportWidth, setViewportWidth] = useState(0);
	const [breakpoint, setBreakpoint] = useState('small');

	const handleResize = useCallback(() => {
		const width = getWindowWidth();
		setViewportWidth(width);
		setBreakpoint(determineScreenSize(width));
	}, []);

	useIsomorphicLayoutEffect(() => {
		on('resize', handleResize);
		handleResize();
		return () => {
			off('resize', handleResize);
		};
	}, [handleResize]);

	return { viewportWidth, breakpoint };
};

function withViewportWidth(WrappedComponent) {
	const WithViewportWidth = (props) => {
		const hookResult = useViewportWidth();
		return (
			<WrappedComponent
				{...props}
				{...hookResult}
			/>
		);
	};
	WithViewportWidth.displayName = `withViewportWidth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithViewportWidth;
}

export default withViewportWidth;
